import { FC, useContext, useState } from 'react';
import {
    Button,
    Tooltip,
    Select,
    Modal,
    Segmented,
    Divider,
    message
} from 'antd';
import {
    EyeInvisibleOutlined,
    DesktopOutlined,
    PlusOutlined,
    CopyOutlined
} from '@ant-design/icons';
import { LayoutEditorContext } from '../../providers/editor/LayoutEditorContext';
import FlexBox from '../atoms/FlexBox';
import { FormType, Entity } from '#types/System.types';
import { AddElementMenu } from './element/AddElementMenu';
import Icon from '../atoms/Icon';
import { DuplicateFormModal } from './DuplicateFormModal';
import { FormDerivesBlockedError } from '../settings/formDerives/FormDerivesBlockedError';
import { FormDerivesUncheckedError } from '../settings/formDerives/FormDerivesUncheckedError';
import { formDeriveCopy } from '@/services/formDerives';

/**
 * Toolbar component for the layout editor providing controls for layout management.
 * Features include:
 * - Layout/Details/Derives mode switching
 * - Structure selection dropdown
 * - Save/Undo/Redo operations
 * - Preview mode controls
 * The Derived values segment's count reads the editor's draft
 * (`layoutEditorState.formDerives`), not Firestore, so it moves with an unsaved
 * add or delete and with Undo.
 * Switching mode here is the "by hand" path, so it clears
 * `visibilityMatrixRow` first: that row is set only by `Open in matrix`, and
 * left standing it would re-highlight and re-scroll the matrix to a stale
 * element on every later visit to Bulk Edit.
 * A save refused by the derive gate (`FormDerivesBlockedError`) is not a failure
 * to report as one: nothing was written, so the toolbar switches to the Derived
 * values pane — where the problem banners and the cycle modal name each entry
 * and its fix — and swallows the error rather than rethrowing it.
 * A save the gate could not JUDGE (`FormDerivesUncheckedError`, the table types
 * still loading) changes no mode at all: there is nothing to look at in the pane
 * and nothing to fix, so it asks for the save again and leaves the admin where
 * they were.
 */
export const LayoutEditorToolbar: FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const [messageApi, contextHolder] = message.useMessage();
    const editor = useContext(LayoutEditorContext);
    if (!editor) throw new Error('EditorToolbar must be used within LayoutEditorProvider');

    const {
        type,
        structures,
        structure,
        isModified,
        canUndo,
        canRedo,
        selectStructure,
        save,
        undo,
        redo,
        mode,
        setMode,
        previewMode,
        setPreviewMode,
        createStructure,
        duplicateStructure,
        hasTableTypeModifications,
        saveTableTypeModifications,
        setVisibilityMatrixRow,
        layoutEditorState,
    } = editor;

    const derivesCount = layoutEditorState.formDerives?.length ?? 0;

    const handleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            await save();
            if (hasTableTypeModifications) {
                await saveTableTypeModifications();
            }
            messageApi.success('Saved successfully');
        } catch (error) {
            if (error instanceof FormDerivesUncheckedError) {
                messageApi.warning(formDeriveCopy.tablesNotLoaded);
                return;
            }
            if (error instanceof FormDerivesBlockedError) {
                setVisibilityMatrixRow(undefined);
                setMode('Derives');
                setPreviewMode('off');
                messageApi.error(formDeriveCopy.saveBlockedByProblems);
                return;
            }
            console.error('Error saving:', error);
            messageApi.error('Failed to save');
            throw error;
        } finally {
            setIsSaving(false);
        }
    };

    const getSelectItems = () => {
        return structures?.map((item: FormType | Entity) => ({
            label: item.description?.shortLabel || item.docId,
            value: item.docId
        }));
    };

    const getCurrentValue = () => {
        return structure?.docId;
    };

    const handleSelect = async (value: string) => {
        if (value === 'new') {
            try {
                const newId = await createStructure();
                await selectStructure(newId);
                setIsSelectOpen(false);
                setMode('Details');
                messageApi.success(`New ${type} created successfully`);
            } catch (error) {
                console.error('Error creating structure:', error);
                messageApi.error(`Failed to create new ${type}`);
                throw error;
            }
            return;
        }

        if (isModified || hasTableTypeModifications) {
            setIsModalOpen(true);
            setPendingId(value);
        } else {
            try {
                await selectStructure(value);
            } catch (error) {
                console.error('Error selecting structure:', error);
                messageApi.error(`Failed to select ${type}`);
                throw error;
            }
        }
    };

    const handleModalConfirm = async () => {
        if (!pendingId) return;

        try {
            await selectStructure(pendingId);
        } catch (error) {
            console.error('Error selecting structure:', error);
            messageApi.error(`Failed to select ${type}`);
            throw error;
        } finally {
            setIsModalOpen(false);
            setPendingId(null);
        }
    };

    const handleDuplicate = async (newName: string) => {
        try {
            const newId = await duplicateStructure(newName);
            await selectStructure(newId);
            setIsDuplicateModalOpen(false);
            messageApi.success(`${type === 'form' ? 'Form' : 'Record'} type duplicated successfully`);
        } catch (error) {
            console.error('Error duplicating structure:', error);
            messageApi.error(`Failed to duplicate ${type} type`);
            throw error;
        }
    };

    return (
        <FlexBox
            id='editor-toolbar'
            justifyStart
            stretch
            noGrow
            gap={10}
            wrap
        >
            {contextHolder}
            <FlexBox noGrow wrap>
                <Select
                    variant='filled'
                    style={{ width: 250 }}
                    value={getCurrentValue()}
                    onChange={handleSelect}
                    placeholder={editor.type === 'form' ? 'Select Form Type' : 'Select Record Type'}
                    open={isSelectOpen}
                    onOpenChange={setIsSelectOpen}
                    options={getSelectItems()}
                    showSearch
                    popupRender={(menu) => (
                        <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                block
                                onClick={() => handleSelect('new')}
                            >
                                Create {editor.type === 'form' ? 'Form Type' : 'Record Type'}...
                            </Button>
                        </>
                    )}
                />
                <Segmented
                    options={[
                        { label: 'Layout', value: 'Layout' },
                        {
                            label: 'Bulk Editor',
                            value: 'BulkEdit',
                            disabled: editor.type !== 'form'
                        },
                        { label: 'Rules', value: 'Details' },
                        {
                            label: derivesCount > 0 ? `Derived values ${derivesCount}` : 'Derived values',
                            value: 'Derives',
                            disabled: editor.type !== 'form'
                        }
                    ]}
                    value={mode}
                    onChange={value => {
                        setVisibilityMatrixRow(undefined);
                        setMode(value as 'Layout' | 'BulkEdit' | 'Details' | 'Derives');
                        if (value !== 'Layout') {
                            setPreviewMode('off');
                        }
                    }}
                    aria-label="Layout editor mode selector"
                />
            </FlexBox>
            {mode === 'Layout' && <AddElementMenu />}
            <FlexBox justifyStart>
                <Tooltip title="Save changes">
                    <Button
                        type="link"
                        icon={<Icon.Save />}
                        disabled={!isModified && !hasTableTypeModifications}
                        loading={isSaving}
                        onClick={handleSave}
                        size='small'
                    >
                        Save
                    </Button>
                </Tooltip>
                <Tooltip title="Undo last change">
                    <Button
                        type='link'
                        icon={<Icon.RotateCcw />}
                        disabled={!canUndo}
                        onClick={undo}
                        size='small'
                    >
                        Undo
                    </Button>
                </Tooltip>
                <Tooltip title="Redo last change">
                    <Button
                        type='link'
                        icon={<Icon.RotateCw />}
                        disabled={!canRedo}
                        onClick={redo}
                        size='small'
                    >
                        Redo
                    </Button>
                </Tooltip>
                <Tooltip title={`Duplicate ${type === 'form' ? 'form' : 'record'} type`}>
                    <Button
                        type='link'
                        icon={<CopyOutlined />}
                        disabled={!structure}
                        onClick={() => setIsDuplicateModalOpen(true)}
                        size='small'
                    >
                        Duplicate
                    </Button>
                </Tooltip>
            </FlexBox>
            <Segmented
                options={[
                    { value: 'off', icon: <EyeInvisibleOutlined /> },
                    { value: 'desktop', icon: <DesktopOutlined /> }
                ]}
                value={previewMode}
                onChange={value => setPreviewMode(value as 'off' | 'desktop')}
                disabled={mode === 'Details' || mode === 'BulkEdit' || mode === 'Derives'}
            />
            <Modal
                title="Unsaved Changes"
                open={isModalOpen}
                onOk={handleModalConfirm}
                onCancel={() => {
                    setIsModalOpen(false);
                    setPendingId(null);
                }}
                okText="Switch"
                cancelText="Cancel"
            >
                You have unsaved changes. Switching layouts will lose these changes. Continue?
            </Modal>
            {structure && (
                <DuplicateFormModal
                    open={isDuplicateModalOpen}
                    onConfirm={handleDuplicate}
                    onCancel={() => setIsDuplicateModalOpen(false)}
                    currentName={structure.description?.shortLabel || structure.docId || 'Untitled'}
                    type={type}
                />
            )}
        </FlexBox>
    );
}; 